import type { BildEntscheidung, TextAntwort } from '@awf/shared';
import { useEffect, useRef, useState } from 'react';
import { klasse } from '../ui/klasse.js';
import { LinienSymbol } from '../ui/linien-symbol.js';
import { istErlaubteBildquelle } from './als-html.js';
import stile from './bild-bewertung.module.css';
import { LupenKnopf } from './bild-lupe.js';

export type TextBild = TextAntwort['bilder'][number];

export function bildName(bild: TextBild, position: number): string {
  return bild.name ?? `Bild ${position + 1}`;
}

/** Wie der finale Ratgeber (Spec §6): das Headerbild als Hero, die uebrigen als Galerie. */
export function istHeaderBild(bild: TextBild): boolean {
  return bild.typ ? bild.typ === 'header_image' : bild.index === 0;
}

export function BildBewertung({
  bild,
  name,
  entscheidung,
  rundenlimitErreicht,
  deaktiviert,
  variante,
  aendern,
  bewertungAussen = false,
}: {
  bild: TextBild;
  name: string;
  entscheidung: BildEntscheidung;
  rundenlimitErreicht: boolean;
  deaktiviert: boolean;
  variante: 'hero' | 'galerie';
  aendern: (aenderung: Partial<BildEntscheidung>) => void;
  /**
   * Bilder-Freigabe (Rueckmeldung 2026-10-13): "Passt", "Ändern" und das Feld stehen dauerhaft
   * in der Rueckmeldespalte rechts statt beim Ueberfahren auf dem Bild. Die Figur zeigt dann nur
   * das Bild mit dem Vergroessern-Zeichen.
   */
  bewertungAussen?: boolean;
}) {
  const [laedtNicht, setLaedtNicht] = useState(false);
  // Angezeigt wird die Drive-Vorschau; `bild.url` zeigt auf den Bildgenerator und faellt durch
  // die Allowlist. Beide laufen durch die Pruefung -- sie ist hier die einzige Verteidigung.
  const vorschau = bild.vorschauUrl ?? bild.url;
  const zeigbar = istErlaubteBildquelle(vorschau) && !laedtNicht;
  const wirdGeaendert = entscheidung.aktion === 'optimize';
  // Das Feld haengt nicht nur am Zustand, sondern auch am Fokus (Abnahme B19): Steht Feedback,
  // klappt es beim Verlassen der Figur zu. Ein leeres Feld bleibt offen, weil das Feedback
  // Pflicht ist. Die Rueckmeldespalte zeigt denselben Text weiter und bleibt unberuehrt.
  const [feldOffen, setFeldOffen] = useState(false);
  const feldSichtbar = wirdGeaendert && (feldOffen || entscheidung.feedback.trim() === '');
  const feld = useRef<HTMLTextAreaElement>(null);
  const fokusAnfordern = useRef(false);
  // Stand beim Oeffnen des Feldes, fuer "Abbrechen" (Rueckmeldung 2026-10-16: Knoepfe wie beim
  // Hinweis zur markierten Stelle). Getippt wird weiter direkt in die Entscheidung, damit der
  // Hauptknopf nie einen nicht uebernommenen Entwurf verliert; "Abbrechen" stellt diesen Stand
  // wieder her. Ohne Stand (Feld kam aus einem geladenen Entwurf) heisst Abbrechen "Passt".
  const standBeimOeffnen = useRef<BildEntscheidung | null>(null);

  useEffect(() => {
    // Das Feld existiert erst nach dem Rendern; darum wird der Fokus hier nachgeholt.
    if (fokusAnfordern.current && feldSichtbar && feld.current) {
      fokusAnfordern.current = false;
      feld.current.focus();
    }
  }, [feldSichtbar]);

  function oeffneFeld(stand: BildEntscheidung = entscheidung) {
    standBeimOeffnen.current = stand;
    fokusAnfordern.current = true;
    setFeldOffen(true);
    // War das Feld schon sichtbar, aendert sich `feldSichtbar` nicht: dann sofort fokussieren.
    if (feldSichtbar && feld.current) {
      fokusAnfordern.current = false;
      feld.current.focus();
    }
  }

  const feldId = `bild-rueckmeldung-${bild.index}`;

  function abbrechen() {
    const stand = standBeimOeffnen.current;
    standBeimOeffnen.current = null;
    setFeldOffen(false);
    if (stand === null || stand.aktion === 'approve') aendern({ aktion: 'approve', feedback: '' });
    else aendern({ feedback: stand.feedback });
  }

  return (
    <figure
      className={klasse(stile.bild, variante === 'hero' && stile.hero)}
      data-bild-index={bild.index}
      onBlur={(e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        // Alt-Tab oder ein Klick in ein anderes Fenster: Der Fokus bleibt in der Figur, sobald das
        // Fenster zurueckkommt. Das ist kein Verlassen.
        if (!document.hasFocus()) return;
        // Der Fokus springt beim Oeffnen der Lupe vom Knopf in der Figur in den Dialog an `body`
        // (Spec 2026-09-30-lupe-bilder §3, Nachtrag). Das ist kein Verlassen der Figur: Nach dem
        // Schliessen kehrt er hierher zurueck.
        if (e.relatedTarget instanceof Element && e.relatedTarget.closest('[data-bild-lupe]')) {
          return;
        }
        if (entscheidung.feedback.trim() !== '') setFeldOffen(false);
      }}
    >
      <div className={stile.flaeche}>
        {zeigbar ? (
          <img src={vorschau} alt={name} loading="lazy" onError={() => setLaedtNicht(true)} />
        ) : (
          // Kein zweiter Name-Text hier (R12-1) -- die Figcaption traegt ihn schon (Spec §6).
          <div className={stile.platzhalter}>
            <span>Vorschau nicht verfügbar</span>
          </div>
        )}
        {wirdGeaendert && !bewertungAussen && (
          <button
            type="button"
            className={stile.abzeichen}
            // WCAG 2.5.3: Der Name beginnt mit dem sichtbaren Text, sonst findet Sprachsteuerung
            // ("Wird geändert klicken") den Knopf nicht.
            aria-label={`Wird geändert, Rückmeldung zu ${name} bearbeiten`}
            // Einstieg fuer "Zum Bild" aus der Rueckmeldespalte (freigabe-page.tsx).
            data-feld-oeffnen
            disabled={deaktiviert}
            onClick={() => oeffneFeld()}
          >
            Wird geändert
          </button>
        )}
        {/* Oben rechts; oben links steht das Abzeichen, unten rechts die Bewertung. Nicht an
            `deaktiviert` gebunden: Ansehen aendert nichts (Spec §4.3). */}
        {zeigbar && (
          <LupenKnopf
            name={name}
            vorschau={vorschau}
            className={klasse(stile.lupe, bewertungAussen && stile.lupeSichtbar)}
          />
        )}
        {!bewertungAussen && (
          <div className={stile.knoepfe} role="group" aria-label={`Bewertung ${name}`}>
            <button
              type="button"
              className="eds-button eds-button--secondary eds-button--sm"
              aria-pressed={!wirdGeaendert}
              disabled={deaktiviert}
              // `feedback: ''` sofort mit: liegen gebliebenes Feedback ginge sonst unsichtbar mit.
              onClick={() => {
                setFeldOffen(false);
                aendern({ aktion: 'approve', feedback: '' });
              }}
            >
              <LinienSymbol name="check" />
              Passt
            </button>
            {!rundenlimitErreicht && (
              <button
                type="button"
                className="eds-button eds-button--secondary eds-button--sm"
                aria-pressed={wirdGeaendert}
                disabled={deaktiviert}
                onClick={() => {
                  aendern({ aktion: 'optimize' });
                  oeffneFeld(entscheidung);
                }}
              >
                <LinienSymbol name="message" />
                Ändern
              </button>
            )}
          </div>
        )}
      </div>
      <figcaption className={stile.name}>{name}</figcaption>
      {feldSichtbar && !bewertungAussen && (
        // Aufbau und Optik wie das Feld "Hinweis zur markierten Stelle" (freigabe-page.tsx):
        // Karte, Feldbeschriftung direkt ueber dem Kasten, Abbrechen und Uebernehmen darunter.
        <div className={stile.feld}>
          <label className={klasse('eds-field__label', stile.frage)} htmlFor={feldId}>
            Was soll anders werden?
          </label>
          <textarea
            id={feldId}
            ref={feld}
            className={klasse('eds-field__input', stile.rueckmeldung)}
            aria-label={`Rückmeldung zu ${name}`}
            value={entscheidung.feedback}
            disabled={deaktiviert}
            onChange={(e) => aendern({ feedback: e.target.value })}
          />
          <div className={stile.aktionen}>
            <button
              type="button"
              className="eds-button eds-button--secondary eds-button--sm"
              disabled={deaktiviert}
              onClick={abbrechen}
            >
              Abbrechen
            </button>
            <button
              type="button"
              className="eds-button eds-button--primary eds-button--sm"
              // Leer gibt es nichts zu uebernehmen: Ein Bild auf "Ändern" braucht eine Rueckmeldung.
              disabled={deaktiviert || entscheidung.feedback.trim() === ''}
              onClick={() => {
                standBeimOeffnen.current = null;
                setFeldOffen(false);
              }}
            >
              Rückmeldung übernehmen
            </button>
          </div>
        </div>
      )}
    </figure>
  );
}
